import { useEffect, useCallback, type ComponentType, type RefObject } from "react";
import { useNavigate } from "react-router-dom";

import { Tag as TagIcon } from "lucide-react";

import { sanitizeHtml } from "@/lib/htmlSanitizer";
import CommentSection from "../CommentSection";
import Logo from "../Logo";
import { PollRenderer } from "../polls/PollRenderer";
import PostLikes from "./PostLikes";

interface BlogPostDetailProps {
  post: {
    id: string;
    title: string;
    content: string;
    preview: string;
    image_url?: string;
    facebook_url?: string;
    twitter_url?: string;
    instagram_url?: string;
    linkedin_url?: string;
    youtube_url?: string;
    tiktok_url?: string;
    threads_url?: string;
    google_business_url?: string;
    pinterest_url?: string;
    snapchat_url?: string;
    bluesky_url?: string;
    reddit_url?: string;
    substack_url?: string;
    discord_url?: string;
    published: boolean;
    published_at?: string;
    created_at: string;
    user_id: string;
    post_tags?: {
      tag_id: string;
      tags: {
        name: string;
      };
    }[];
  };
  commentsRef: RefObject<HTMLDivElement>;
  formatDate: (dateString: string) => string;
  getSocialIcon: (platform: string) => string | ComponentType<{ className?: string }> | null;
  targetSection?: string | null;
  onScrollToSection?: (section: "poll" | "comments") => void;
}

const BlogPostDetail = ({
  post,
  commentsRef,
  formatDate,
  getSocialIcon,
  targetSection,
  onScrollToSection,
}: BlogPostDetailProps) => {
  const navigate = useNavigate();

  // Intercept clicks on internal links to prevent full page refresh
  const handleContentClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    const anchor = target.closest('a');
    
    if (!anchor) return;
    
    const href = anchor.getAttribute('href');
    if (!href) return;
    
    // Check if it's an internal link (relative or same origin)
    const isRelative = href.startsWith('/') || href.startsWith('?');
    const isSameOrigin = href.startsWith(window.location.origin);
    
    if (isRelative || isSameOrigin) {
      e.preventDefault();
      
      // Parse the URL to extract post and section
      const url = new URL(href, window.location.origin);
      const postId = url.searchParams.get('post');
      const section = url.searchParams.get('section') as "poll" | "comments" | null;
      
      // If it's a link to the current post with a section, just scroll
      if (postId === post.id && section && onScrollToSection) {
        onScrollToSection(section);
      } else {
        // Navigate using React Router (no page refresh)
        navigate(url.pathname + url.search);
      }
    }
  }, [navigate, post.id, onScrollToSection]);

  return (
    <div className="p-4">
      <div className="space-y-6">
        <div className="flex flex-col min-[1100px]:grid min-[1100px]:grid-cols-[minmax(0,1fr)_minmax(650px,725px)_minmax(0,1fr)] min-[1100px]:gap-3 mb-6 items-center min-[1100px]:items-start">
          <div className="flex flex-col items-center min-[1100px]:items-end min-[1100px]:pr-2 flex-shrink-0 min-[1100px]:col-start-1">
            <div className="w-56 max-h-96 overflow-hidden rounded bg-muted/30">
              {post.image_url ? (
                <img
                  src={post.image_url.replace(/\.(\w+)$/, "-medium.png")}
                  alt={post.title}
                  className="w-full h-auto max-h-96 object-contain"
                  onError={(e) => {
                    e.currentTarget.src = post.image_url!;
                  }}
                />
              ) : (
                <div className="w-56 h-56 bg-muted/50 flex items-center justify-center opacity-30">
                  <Logo size={96} className="grayscale" />
                </div>
              )}
            </div>
          </div>

          <div className="w-full max-w-[725px] min-[1100px]:col-start-2 min-[1100px]:pl-2">
            <h1 className="text-xl font-bold mb-2">{post.title}</h1>
            <p className="text-sm text-muted-foreground mb-2">
              By Admin • {formatDate(post.published_at || post.created_at)}
            </p>
            {post.post_tags && post.post_tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {post.post_tags.map((pt) => (
                  <span
                    key={pt.tag_id}
                    className="inline-flex items-center gap-1 bg-primary/10 text-primary border border-primary/20 rounded-full px-3 py-1 text-sm"
                  >
                    <TagIcon className="w-3 h-3" />
                    {pt.tags.name}
                  </span>
                ))}
              </div>
            )}
            <PostLikes
              postId={post.id}
              content={post.content}
              post={post}
              isDetailView={true}
              getSocialIcon={getSocialIcon}
            />
            <div
              className="prose prose-sm max-w-none text-sm leading-relaxed prose-headings:text-foreground prose-p:text-foreground prose-a:text-primary prose-strong:text-foreground prose-em:text-foreground prose-code:text-foreground prose-pre:text-foreground prose-blockquote:text-foreground prose-li:text-foreground prose-img:rounded-md prose-img:max-w-full mt-4"
              onClick={handleContentClick}
              dangerouslySetInnerHTML={{
                __html: sanitizeHtml(post.content),
              }}
            />
          </div>
        </div>

        <div className="max-w-[725px] mx-auto w-full space-y-6">
          <div id="poll-section" className="scroll-mt-4">
            <PollRenderer postId={post.id} />
          </div>

          <div id="comments-section" ref={commentsRef} className="border-t pt-4">
            <CommentSection blogPostId={post.id} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogPostDetail;
